/**
 * As perícias do Mistik Island, agrupadas pelo que elas fazem.
 *
 * <p><b>Os rótulos são os do alpha</b>, a versão que está no ar: Contusão (e não Maça),
 * Arma Exótica, Defesa Arcana. Onde o jogo e este documento discordavam, o jogo ganha —
 * e as duas primeiras saíram depois, dando lugar a Magia Ofensiva (ver abaixo) —
 * quem lê a tela do alpha e depois esta precisa reconhecer a mesma perícia.
 *
 * <p><b>Os ids não seguem o rótulo.</b> `maca` continua `maca` com o rótulo Contusão,
 * porque o id é a chave da rolagem já rascunhada e do ícone; trocá-lo apagaria o que o
 * time escreveu sem nada passar a funcionar melhor. É a mesma razão de `Pericia.java`
 * guardar o id e nunca o rótulo.
 *
 * <p><b>A lista não está fechada.</b> São dezoito hoje, e o time acrescenta conforme o
 * jogo cresce — por isso nada aqui (nem título, nem texto de tela) conta quantas são.
 * Um número escrito à mão vira mentira no primeiro acréscimo.
 *
 * <p>As cores das seis que coletam e criam são as cadastradas em `Pericia.java`. As de
 * Combate não existem no backend: elas moram só na Ficha, que é documento de regra e
 * não wiki de mundo.
 */
export interface Pericia {
  id: string;
  rotulo: string;
  cor: string;
  /**
   * Existe no desenho, mas <b>não na Season 1</b>.
   *
   * <p>Fica na lista de propósito, em cinza e desativada, em vez de sair dela: quem
   * abre a tela precisa saber que a perícia está prevista — apagá-la faria o time
   * propor de novo o que já foi decidido. E, no que é editável (a marcação por área, a
   * equação), ela recusa entrada: marcar Tecnomagia num hexágono da Season 1 seria
   * escrever um dado que contradiz o próprio recorte da tela.
   */
  depoisDaSeason1?: boolean;
}

/** A cor de quem ainda não entrou: sem matiz, para não competir com quem já vale. */
export const COR_INATIVA = '#6b6f6c';

/** A cor que a tela deve usar — cinza para o que ainda não entrou. */
export const corDe = (p: Pericia) => (p.depoisDaSeason1 ? COR_INATIVA : p.cor);

/*
 * As de Combate herdam a cor da categoria em vez de terem uma cada.
 *
 * <p>Onze cores diferentes fariam o grupo parecer onze assuntos. O que distingue uma
 * espada de um machado é o <b>ícone</b>; o que a cor precisa dizer é "isto é combate" —
 * e, dentro dele, de que lado da troca de golpes a perícia está.
 */
const OFENSIVA = '#c85b54';
const DEFENSIVA = '#7f9bb5';

export const COMBATE_OFENSIVAS: Pericia[] = [
  { id: 'desarmado', rotulo: 'Desarmado', cor: OFENSIVA },
  { id: 'espada', rotulo: 'Espada', cor: OFENSIVA },
  { id: 'machado', rotulo: 'Machado', cor: OFENSIVA },
  { id: 'haste', rotulo: 'Haste', cor: OFENSIVA },
  // "Contusão" é como o alpha a chama. O id continua `maca` — ele é a chave da rolagem
  // já rascunhada, e trocá-lo apagaria o que o time escreveu para ganhar nada.
  { id: 'maca', rotulo: 'Contusão', cor: OFENSIVA },
  { id: 'arco', rotulo: 'Arco', cor: OFENSIVA },
  { id: 'besta', rotulo: 'Besta', cor: OFENSIVA, depoisDaSeason1: true },
  /*
   * Magia Ofensiva entrou no lugar de Duas Mãos e Arma Exótica — decisão do Theo ao
   * montar as fichas do Mistik Dream. As duas saíram sem perda: estavam marcadas EM
   * BREVE, e o que é EM BREVE recusa entrada nas telas de Game Design, então não havia
   * rolagem nem marcação gravada em nenhuma das duas.
   *
   * <p>Continua EM BREVE *aqui*: o GDD lista magia como "sistema inteiro em aberto", e a
   * Season 1 do jogo não a tem. A mesa de Mistik Dream a usa mesmo assim — é para isso
   * que uma mesa experimental serve —, e as fichas de lá ignoram este marcador.
   */
  { id: 'magiaOfensiva', rotulo: 'Magia Ofensiva', cor: OFENSIVA, depoisDaSeason1: true },
];

export const COMBATE_DEFENSIVAS: Pericia[] = [
  { id: 'esquiva', rotulo: 'Esquiva', cor: DEFENSIVA },
  { id: 'bloqueio', rotulo: 'Bloqueio', cor: DEFENSIVA },
  // O id continua `arcana`: trocá-lo apagaria o que estivesse gravado sob ele para
  // ganhar nada — a mesma razão de `maca` continuar `maca` sendo "Contusão".
  { id: 'arcana', rotulo: 'Magia Defensiva', cor: DEFENSIVA, depoisDaSeason1: true },
];

export const COMBATE: Pericia[] = [...COMBATE_OFENSIVAS, ...COMBATE_DEFENSIVAS];

/*
 * O id de Esfolamento continua `esfolagem`, e vai continuar.
 *
 * <p>Ele é a mesma palavra em três lugares que não se movem juntos: o `check` de
 * `item.subcategoria`, o enum `Pericia` do backend e a coluna `pericia` do mundo. O
 * rótulo é do alpha; o id é do banco.
 */
export const COLETAS: Pericia[] = [
  { id: 'herbalismo', rotulo: 'Herbalismo', cor: '#54b87a' },
  { id: 'mineracao', rotulo: 'Mineração', cor: '#8fa6b8' },
  { id: 'esfolagem', rotulo: 'Esfolamento', cor: '#c89a6a' },
];

/**
 * As criações.
 *
 * <p><b>O pareamento um-para-um acabou.</b> Alquimia, Forja e Couraria continuam saindo
 * de Herbalismo, Mineração e Esfolamento — mas Tecnomagia não tem coleta própria, e é o
 * que quebra a simetria que a tela afirmava antes. Quem lê `COLETAS[i]` e `CRIACOES[i]`
 * ainda encontra o par nas três primeiras; a quarta não tem par, e é assim de propósito.
 *
 * <p><b>Tecnomagia é criação e, ainda assim, só entra depois da Season 1.</b> As duas
 * coisas convivem, e vale deixar escrito porque já se desfizeram uma na outra: eu li
 * "ela é criação como Forja" como se fosse permissão para tirar o `depoisDaSeason1`, e o
 * Theo teve de mandar pôr de volta. <b>A categoria diz o que a perícia faz; a marca diz
 * quando ela entra.</b> São perguntas independentes — só o Theo responde a segunda, e
 * hoje a resposta é: depois da Season 1, em cinza na Personagem e na Tier List.
 */
export const CRIACOES: Pericia[] = [
  { id: 'alquimia', rotulo: 'Alquimia', cor: '#a87bd0' },
  { id: 'forja', rotulo: 'Forja', cor: '#e0913a' },
  { id: 'couraria', rotulo: 'Couraria', cor: '#8b6b45' },
  { id: 'tecnomagia', rotulo: 'Tecnomagia', cor: '#4fb3c4', depoisDaSeason1: true },
];

/**
 * As perícias que só a ficha da mesa usa — pedido do Theo, 19/08/2026.
 *
 * <p>Moram aqui, e não em `mistikdream`, pela regra de sempre: o vocabulário de perícia
 * é do Game Design, e uma segunda lista divergiria no primeiro renome — foi assim que
 * Magia Ofensiva entrou, quando as fichas do Mistik Dream foram montadas.
 *
 * <p>Mas ficam <b>fora</b> de {@link TODAS_AS_PERICIAS}, e de propósito: aquela lista é
 * "todas, como a Ficha as apresenta", e é dela que as rolagens em branco nascem —
 * incluir as quatro semearia equações que nenhuma tela edita. Quem as junta às demais é
 * a ficha do Mistik Dream, o único lugar que as apresenta. No desenho do jogo elas são
 * o teste de Viajar e as subações do Descansar (Medicina, Cozinhar, Estudar).
 */
export const OUTRAS: Pericia[] = [
  { id: 'viajar', rotulo: 'Viajar', cor: '#6b9e6a' },
  { id: 'medicina', rotulo: 'Medicina', cor: '#c96a7c' },
  { id: 'cozinhar', rotulo: 'Cozinhar', cor: '#d9a441' },
  { id: 'estudar', rotulo: 'Estudar', cor: '#7d8fc9' },
];

/** As que valem agora — o que a Tier List da Season 1 pode marcar e medir. */
export const naSeason1 = (lista: Pericia[]) => lista.filter((p) => !p.depoisDaSeason1);

/**
 * As que se marcam por área — as colunas da tabela e os ícones do mapa.
 *
 * <p>Combate fica de fora, e não por esquecimento: marcar "Espada" num hexágono não
 * diria nada que o tier já não diga. O mapa responde <i>o que se tira dali</i>, e
 * nenhuma perícia de combate tira nada.
 */
export const PERICIAS: Pericia[] = [...COLETAS, ...CRIACOES];

/** Todas, como a Ficha as apresenta. */
export const TODAS_AS_PERICIAS: Pericia[] = [...COMBATE, ...COLETAS, ...CRIACOES];

export const periciaPorId = (id: string) => TODAS_AS_PERICIAS.find((p) => p.id === id);

/** Os dados que a equação aceita. Universais — não são invenção deste projeto. */
export const DADOS = ['d4', 'd6', 'd8', 'd10', 'd12', 'd20', 'd100'];

/**
 * A equação de uma perícia: dois atributos e um dado.
 *
 * <p>A perícia não está aqui porque <b>ela é a chave</b>: cada uma tem a sua equação, e
 * guardá-la dentro do valor seria repetir a chave do lado de dentro — dois lugares
 * dizendo a mesma coisa, e um deles podendo mentir.
 *
 * <p>Os atributos guardam a <b>sigla</b> (`FOR`, `PER`…), e não o nome por extenso.
 * Renomear "Vitalidade" na Ficha não pode invalidar uma equação já montada — é a mesma
 * razão de `Pericia.java` guardar o id e nunca o rótulo.
 */
export interface Equacao {
  atributoA: string;
  atributoB: string;
  dado: string;
}

/**
 * O número que cada tier exige. Chave é o tier, valor é o que o time digitou.
 *
 * <p><b>Uma equação por perícia, uma tabela de tier por grupo.</b> A assimetria é do
 * jogo, não do código: cada perícia rola com atributos próprios (minerar é força e
 * vigor; herbalismo é percepção e mente), mas a <i>dificuldade</i> de um T3 é do tier,
 * e não de quem está tentando — se ela variasse por perícia, "T3" deixaria de
 * significar a mesma coisa nas colunas do mapa.
 *
 * <p>As equações não moram mais aqui: elas são as mesmas da Personagem, e vivem em
 * `rolagens.ts`. Guardá-las nos dois lugares era a régua duplicada de sempre.
 */
export type ExigenciaPorTier = Record<number, string>;

export const TIERS = [1, 2, 3, 4, 5];

export const equacaoEmBranco = (): Equacao => ({ atributoA: '', atributoB: '', dado: 'd20' });

export const tiersEmBranco = (): ExigenciaPorTier =>
  Object.fromEntries(TIERS.map((t) => [t, ''])) as ExigenciaPorTier;
